/**
 * One zod schema per server→client event.
 *
 * `03-ARCHITECTURE.md` §4: every payload is validated on both sides. The
 * `satisfies` clause below is the important part — it fails to compile if
 * `ServerToClientEvents` gains an event with no schema, so a contract change on
 * the server side cannot silently reach the store unvalidated.
 */
import type { ServerToClientEvents } from "@crashx/shared";
import {
  BalanceEvent,
  BetsBatchEvent,
  ChatMessage,
  ChatPage,
  ChatRemoved,
  ChatRestriction,
  ChatAnnouncement,
  RainReceived,
  MyBet,
  NoticeEvent,
  OnlineEvent,
  RainEvent,
  RoundBettingEvent,
  RoundCrashEvent,
  RoundStartEvent,
  RoundTickEvent,
  ServerTimeEvent,
  StateSync,
} from "@crashx/shared";
import type { ZodType } from "zod";

export type ServerEventName = keyof ServerToClientEvents;

/** Payload type of a server event. */
export type ServerEventPayload<K extends ServerEventName> = Parameters<ServerToClientEvents[K]>[0];

export const SERVER_EVENT_SCHEMAS = {
  "state:sync": StateSync,
  "round:betting": RoundBettingEvent,
  "round:start": RoundStartEvent,
  "round:tick": RoundTickEvent,
  "round:crash": RoundCrashEvent,
  "bets:batch": BetsBatchEvent,
  "me:balance": BalanceEvent,
  "me:bet": MyBet,
  "players:online": OnlineEvent,
  "chat:message": ChatMessage,
  "chat:rain": RainEvent,
  "chat:history": ChatPage,
  "chat:removed": ChatRemoved,
  "chat:restriction": ChatRestriction,
  "chat:rain:received": RainReceived,
  "chat:announcement": ChatAnnouncement,
  "server:time": ServerTimeEvent,
  "server:notice": NoticeEvent,
} as const satisfies {
  [K in ServerEventName]: ZodType<ServerEventPayload<K>>;
};

export const SERVER_EVENT_NAMES = Object.keys(SERVER_EVENT_SCHEMAS) as ServerEventName[];
